import React from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Controller, useFormContext } from "react-hook-form";
import { SortableList } from "shared/SortableList";
import { SortableItem } from "shared/SortableItem";
import TranslatedTextField from "./TranslatedTextField";
import AnswersSection from "./AnswersSection";
import { LessonFormData, emptyLocalizedText } from "../types";
import { useSortableFieldArray } from "../hooks/useSortableFieldArray";
import { useActiveLocale } from "../ActiveLocaleContext";

type Props = { partIndex: number };

const QuestionsSection: React.VFC<Props> = ({ partIndex }) => {
  const { control } = useFormContext<LessonFormData>();
  const { activeLocale } = useActiveLocale();
  const { ids, fields, onDragEnd, append, remove } = useSortableFieldArray(
    `parts.${partIndex}.questions` as const
  );

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center">
        <Typography variant="h6">Questions</Typography>
        <Button
          startIcon={<Add />}
          size="small"
          onClick={() =>
            append({
              title: emptyLocalizedText(),
              tipDescription: emptyLocalizedText(),
              answers: [
                { correct: true, text: emptyLocalizedText() },
                { correct: false, text: emptyLocalizedText() },
              ],
            })
          }
        >
          Add question
        </Button>
      </Box>

      <SortableList items={ids} onDragEnd={onDragEnd}>
        {fields.map((_f, qi) => (
          <SortableItem
            key={ids[qi]}
            id={ids[qi]}
            accordion
            label={`Question ${qi + 1}`}
            onRemove={() => remove(qi)}
          >
            <Controller
              control={control}
              name={`parts.${partIndex}.questions.${qi}.title` as const}
              render={({ onChange, value }) => (
                <TranslatedTextField
                  label="Question title"
                  value={value ?? emptyLocalizedText()}
                  onChange={onChange}
                  activeLocale={activeLocale}
                  autoTranslate
                  multiline
                  rows={2}
                />
              )}
            />
            <Controller
              control={control}
              name={`parts.${partIndex}.questions.${qi}.tipDescription` as const}
              render={({ onChange, value }) => (
                <TranslatedTextField
                  label="Tip (shown after answering)"
                  value={value ?? emptyLocalizedText()}
                  onChange={onChange}
                  activeLocale={activeLocale}
                  autoTranslate
                  multiline
                  rows={2}
                />
              )}
            />
            <AnswersSection partIndex={partIndex} questionIndex={qi} />
          </SortableItem>
        ))}
      </SortableList>
    </Box>
  );
};

export default QuestionsSection;
